import { useState, useEffect } from "react";
import { Layout } from "@/pages/Dashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Plus, Edit2, Trash2, Music2, Users, Calendar, 
  GripVertical, ChevronDown, ChevronUp, ExternalLink, 
  Copy, Loader2, Search
} from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { Kicker } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";

export const SetlistLibrary = () => {
  const [setlists, setSetlists] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingSetlist, setEditingSetlist] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedSetlist, setExpandedSetlist] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    collaborator_ids: [],
    tunes: []
  });

  useEffect(() => {
    fetchSetlists();
    fetchCollaborators();
  }, []);

  const fetchSetlists = async () => {
    try {
      const response = await apiClient.get("/setlists");
      setSetlists(response.data);
    } catch (error) {
      toast.error("Failed to fetch setlists");
    } finally {
      setLoading(false);
    }
  };

  const fetchCollaborators = async () => {
    try {
      const response = await apiClient.get("/collaborators");
      setCollaborators(response.data);
    } catch (error) {
      console.error("Failed to fetch collaborators");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      collaborator_ids: [],
      tunes: []
    });
    setEditingSetlist(null);
  };

  const handleOpenDialog = (setlist = null) => {
    if (setlist) {
      setEditingSetlist(setlist);
      setFormData({
        name: setlist.name || "",
        description: setlist.description || "",
        collaborator_ids: setlist.collaborator_ids || [],
        tunes: setlist.tunes || []
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Please enter a setlist name");
      return;
    }

    setSaving(true);
    try {
      if (editingSetlist) {
        await apiClient.put(`/setlists/${editingSetlist.setlist_id}`, formData);
        toast.success("Setlist updated");
      } else {
        await apiClient.post("/setlists", formData);
        toast.success("Setlist created");
      }
      setShowDialog(false);
      resetForm();
      fetchSetlists();
    } catch (error) {
      toast.error("Failed to save setlist");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (setlistId) => {
    if (!confirm("Are you sure you want to delete this setlist?")) return;
    
    try {
      await apiClient.delete(`/setlists/${setlistId}`);
      toast.success("Setlist deleted");
      fetchSetlists();
    } catch (error) {
      toast.error("Failed to delete setlist");
    }
  };

  const addTune = () => {
    setFormData(prev => ({
      ...prev,
      tunes: [...prev.tunes, { tune_name: "", key: "", tempo: "", notes: "", chart_url: "", audio_url: "", order: prev.tunes.length }]
    }));
  };

  const updateTune = (index, field, value) => {
    setFormData(prev => {
      const tunes = [...prev.tunes];
      tunes[index] = { ...tunes[index], [field]: value };
      return { ...prev, tunes };
    });
  };

  const removeTune = (index) => {
    setFormData(prev => ({
      ...prev,
      tunes: prev.tunes.filter((_, i) => i !== index)
    }));
  };

  const moveTune = (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === formData.tunes.length - 1)) {
      return;
    }
    
    setFormData(prev => {
      const tunes = [...prev.tunes];
      const temp = tunes[index];
      tunes[index] = tunes[index + direction];
      tunes[index + direction] = temp;
      return { ...prev, tunes };
    });
  };

  const toggleCollaborator = (collabId) => {
    setFormData(prev => {
      const ids = prev.collaborator_ids.includes(collabId)
        ? prev.collaborator_ids.filter(id => id !== collabId)
        : [...prev.collaborator_ids, collabId];
      return { ...prev, collaborator_ids: ids };
    });
  };

  const getCollaboratorNames = (ids) => {
    return ids.map(id => {
      const collab = collaborators.find(c => c.collaborator_id === id);
      return collab?.name || id;
    }).join(", ");
  };

  const filteredSetlists = setlists.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div data-testid="setlist-library-page">
        {/* Header — kicker own row, h1 + New Setlist button on second row */}
        <div style={{ marginBottom: tokens.space.xxl }}>
          <Kicker color={tokens.color.orange} style={{ marginBottom: tokens.space.xs }}>
            Setlist Library
          </Kicker>
          <div
            className="flex flex-row items-center justify-between"
            style={{ gap: tokens.space.md }}
          >
            <h1
              style={{
                margin: 0,
                ...tokens.type.heroSm,
                color: tokens.color.text,
                textWrap: "balance",
                minWidth: 0,
              }}
            >
              Your Setlists
            </h1>
            <button
              type="button"
              onClick={() => handleOpenDialog()}
              data-testid="add-setlist-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: tokens.space.sm,
                padding: "10px 16px",
                background: tokens.color.orange,
                color: tokens.color.onOrange,
                border: tokens.border.brand,
                borderRadius: tokens.radius.md + 1,
                fontFamily: tokens.font.body,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <Plus className="h-4 w-4" />
              New Setlist
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md" style={{ marginBottom: tokens.space.xl }}>
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10"
            style={{ color: tokens.color.textMuted }}
          />
          <Input
            placeholder="Search setlists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-setlists"
          />
        </div>

        {/* Setlists Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div
                key={i}
                className="animate-pulse"
                style={{
                  height: 192,
                  background: tokens.color.cardBg,
                  border: tokens.border.card,
                  borderRadius: tokens.radius.xl,
                }}
              />
            ))}
          </div>
        ) : filteredSetlists.length === 0 ? (
          <div
            style={{
              background: tokens.color.cardBg,
              border: tokens.border.card,
              borderRadius: tokens.radius.xl,
              padding: `${tokens.space.xxxl}px ${tokens.space.xl}px`,
              textAlign: "center",
            }}
          >
            <Music2
              style={{
                width: 48,
                height: 48,
                color: tokens.color.textMuted,
                margin: "0 auto",
                marginBottom: tokens.space.lg,
              }}
            />
            <h3
              style={{
                margin: 0,
                marginBottom: tokens.space.sm,
                ...tokens.type.titleSm,
                color: tokens.color.text,
              }}
            >
              No setlists yet
            </h3>
            <p
              style={{
                margin: 0,
                marginBottom: tokens.space.lg,
                ...tokens.type.body,
                color: tokens.color.textMuted,
              }}
            >
              Create reusable setlists that you can quickly apply to your gigs
            </p>
            <button
              type="button"
              onClick={() => handleOpenDialog()}
              data-testid="add-first-setlist"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: tokens.space.sm,
                padding: "10px 16px",
                background: tokens.color.orange,
                color: tokens.color.onOrange,
                border: tokens.border.brand,
                borderRadius: tokens.radius.md + 1,
                fontFamily: tokens.font.body,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <Plus className="h-4 w-4" />
              Create Your First Setlist
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSetlists.map(setlist => {
              const tk = tokens;
              return (
                <div
                  key={setlist.setlist_id}
                  style={{
                    background: tk.color.cardBg,
                    border: tk.border.card,
                    borderRadius: tk.radius.xl,
                    padding: tk.space.lg,
                    transition: `border-color ${tk.motion.base}`,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = tk.color.orangeLine)}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = tk.color.cardBorder)}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: tk.space.md, gap: tk.space.sm }}>
                    <div style={{ display: "flex", alignItems: "center", gap: tk.space.md, minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: tk.radius.md,
                          background: tk.color.orangeSoft,
                          border: tk.border.brandSoft,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Music2 style={{ width: 18, height: 18, color: tk.color.orange }} />
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <h3
                          style={{
                            margin: 0,
                            ...tk.type.titleSm,
                            color: tk.color.text,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {setlist.name}
                        </h3>
                        <p style={{ margin: 0, marginTop: 2, ...tk.type.bodySm, color: tk.color.textDim }}>
                          {setlist.tunes?.length || 0} tune{(setlist.tunes?.length || 0) === 1 ? "" : "s"}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => handleOpenDialog(setlist)}
                        data-testid={`edit-setlist-${setlist.setlist_id}`}
                        title="Edit"
                        style={{
                          width: 32, height: 32,
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          background: "transparent", border: "none",
                          color: tk.color.textMuted, borderRadius: tk.radius.md, cursor: "pointer",
                          transition: `color ${tk.motion.fast}`,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = tk.color.text)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = tk.color.textMuted)}
                      >
                        <Edit2 style={{ width: 14, height: 14 }} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(setlist.setlist_id)}
                        data-testid={`delete-setlist-${setlist.setlist_id}`}
                        title="Delete"
                        style={{
                          width: 32, height: 32,
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          background: "transparent", border: "none",
                          color: tk.color.textMuted, borderRadius: tk.radius.md, cursor: "pointer",
                          transition: `color ${tk.motion.fast}`,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = tk.color.red)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = tk.color.textMuted)}
                      >
                        <Trash2 style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                  </div>

                  {setlist.description && (
                    <p
                      className="line-clamp-2"
                      style={{
                        margin: 0,
                        marginBottom: tk.space.md,
                        ...tk.type.bodySm,
                        color: tk.color.textDim,
                      }}
                    >
                      {setlist.description}
                    </p>
                  )}

                  {/* Linked Collaborators */}
                  {setlist.collaborator_ids?.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: tk.space.sm, marginBottom: tk.space.md }}>
                      <Users style={{ width: 14, height: 14, color: tk.color.textMuted, flexShrink: 0 }} />
                      <span style={{ ...tk.type.bodySm, color: tk.color.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {getCollaboratorNames(setlist.collaborator_ids)}
                      </span>
                    </div>
                  )}

                  {/* Usage Stats */}
                  <div style={{ display: "flex", alignItems: "center", gap: tk.space.lg, fontSize: 12, color: tk.color.textMuted }}>
                    {setlist.times_used > 0 && (
                      <span>Used {setlist.times_used}x</span>
                    )}
                    {setlist.last_used && (
                      <span>Last: {new Date(setlist.last_used).toLocaleDateString()}</span>
                    )}
                  </div>

                  {/* Expandable Tune List */}
                  <button
                    type="button"
                    onClick={() => setExpandedSetlist(expandedSetlist === setlist.setlist_id ? null : setlist.setlist_id)}
                    style={{
                      width: "100%",
                      marginTop: tk.space.md,
                      padding: "6px 10px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      background: "transparent",
                      border: "none",
                      color: tk.color.textDim,
                      ...tk.type.bodySm,
                      fontWeight: 500,
                      borderRadius: tk.radius.md,
                      cursor: "pointer",
                      transition: `color ${tk.motion.fast}, background ${tk.motion.fast}`,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = tk.color.text; e.currentTarget.style.background = tk.color.innerBg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = tk.color.textDim; e.currentTarget.style.background = "transparent"; }}
                  >
                    {expandedSetlist === setlist.setlist_id ? (
                      <>
                        <ChevronUp style={{ width: 14, height: 14 }} />
                        Hide Tunes
                      </>
                    ) : (
                      <>
                        <ChevronDown style={{ width: 14, height: 14 }} />
                        Show Tunes
                      </>
                    )}
                  </button>

                  {expandedSetlist === setlist.setlist_id && setlist.tunes?.length > 0 && (
                    <div style={{ marginTop: tk.space.md, paddingTop: tk.space.md, borderTop: tk.border.hairline, display: "flex", flexDirection: "column", gap: 4 }}>
                      {setlist.tunes.map((tune, index) => (
                        <div key={index} style={{ display: "flex", alignItems: "center", gap: tk.space.sm, ...tk.type.bodySm, color: tk.color.text }}>
                          <span style={{ color: tk.color.textMuted, width: 20 }}>{index + 1}.</span>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{tune.tune_name}</span>
                          {tune.key && (
                            <span
                              style={{
                                fontFamily: tk.font.mono,
                                fontSize: 11,
                                color: tk.color.textDim,
                                background: tk.color.innerBg,
                                border: tk.border.inner,
                                borderRadius: tk.radius.sm,
                                padding: "2px 6px",
                              }}
                            >
                              {tune.key}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSetlist ? "Edit Setlist" : "Create Setlist"}</DialogTitle>
              <DialogDescription>
                Create a reusable setlist that you can apply to multiple gigs
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Setlist Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="setlist-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="setlist-description"
                  />
                </div>
              </div>

              {/* Link to Collaborators */}
              {collaborators.length > 0 && (
                <div className="space-y-2">
                  <Label>Link to Band Members</Label>
                  <div className="flex flex-wrap gap-2">
                    {collaborators.map(collab => (
                      <Badge
                        key={collab.collaborator_id}
                        variant={formData.collaborator_ids.includes(collab.collaborator_id) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => toggleCollaborator(collab.collaborator_id)}
                      >
                        {collab.name}
                        {collab.instrument && ` (${collab.instrument})`}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Click to select band members this setlist is typically used with
                  </p>
                </div>
              )}

              {/* Tunes */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Tunes</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addTune}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Tune
                  </Button>
                </div>

                <ScrollArea className="max-h-[300px]">
                  <div className="space-y-3">
                    {formData.tunes.map((tune, index) => (
                      <div key={index} className="p-3 bg-secondary/30 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => moveTune(index, -1)}
                              disabled={index === 0}
                            >
                              <ChevronUp className="h-3 w-3" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => moveTune(index, 1)}
                              disabled={index === formData.tunes.length - 1}
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </div>
                          <span className="text-sm text-muted-foreground w-6">{index + 1}.</span>
                          <Input
                            value={tune.tune_name}
                            onChange={(e) => updateTune(index, "tune_name", e.target.value)}
                            className="flex-1"
                            data-testid={`tune-name-${index}`}
                          />
                          <Input
                            value={tune.key || ""}
                            onChange={(e) => updateTune(index, "key", e.target.value)}
                            className="w-24"
                          />
                          <Input
                            value={tune.tempo || ""}
                            onChange={(e) => updateTune(index, "tempo", e.target.value)}
                            className="w-24"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => removeTune(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 ml-12">
                          <Input
                            value={tune.notes || ""}
                            onChange={(e) => updateTune(index, "notes", e.target.value)}
                            className="text-sm"
                          />
                          <Input
                            value={tune.chart_url || ""}
                            onChange={(e) => updateTune(index, "chart_url", e.target.value)}
                            className="text-sm"
                          />
                          <Input
                            value={tune.audio_url || ""}
                            onChange={(e) => updateTune(index, "audio_url", e.target.value)}
                            className="text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                {formData.tunes.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No tunes yet. Click "Add Tune" to get started.
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="bg-primary text-primary-foreground"
                  data-testid="save-setlist"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingSetlist ? "Save Changes" : "Create Setlist"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SetlistLibrary;
